/**
 * Every Convex call that fails in the browser lands on Platform Admin → Errors.
 *
 * A mutation that is refused before it runs — an argument that the validator
 * does not know, a function the deployed bundle no longer has, an auth
 * failure — never reaches a handler, so nothing server-side can log it. The
 * form that called it usually toasts a stripped-down message and moves on,
 * and the platform never hears about it (the food menu form sent a field the
 * create mutation did not accept for a whole day before anyone knew).
 *
 * The Convex client logs each failed call through its logger as
 *   "[CONVEX M(products/creation:createProduct)] [Request ID: …] Server Error\n…"
 * so one listener on that logger sees every failure in the app, with the
 * function name and request id, without touching any form.
 *
 * What is NOT reported here:
 *  - ConvexError payloads. Those are deliberate: a business rule the user
 *    reads ("room already taken"), or a surfaced internal failure that the
 *    server has already written to the log itself (helpers/surfaceErrors.ts).
 *  - The till's `completeSale`, which reports its own failures with the cart
 *    context attached (components/pos/CartPanel.tsx).
 *  - The reporter's own mutation — a failure there must not loop.
 * The same failure is reported once a minute at most.
 */

import type { ConvexReactClient } from "convex/react";
import { api } from "@/convex/_generated/api";
import type { Id } from "@/convex/_generated/dataModel";
import { isConnectionDrop, type NetDiagnostics } from "@/lib/config/errorInsights";

let reportingBrandId: string | null = null;

/** The brand whose pages are open — set by BrandProvider, read by every report. */
export function setReportingBrandId(id: string | null | undefined): void {
  reportingBrandId = id ?? null;
}

export function getReportingBrandId(): string | null {
  return reportingBrandId;
}

const LOG_LINE = /^\[CONVEX ([MAQ])\(([^)]+)\)\]\s*([\s\S]*)$/;
const REPEAT_WINDOW_MS = 60_000;

/** Functions that report themselves (with better context) or must not loop. */
const SELF_REPORTING = ["platformAdmin/errorLog:", "pos/sales:completeSale"];

/**
 * Calls the app makes by itself, on a timer — nobody pressed anything. When the
 * connection drops under one, nobody saw a thing and the next tick retries, so
 * it is not reported (1 Nov 2026: these were nine in ten rows on the Errors
 * page, burying everything else).
 */
const BACKGROUND_CALLS = new Set([
  "infrastructure/dashboardCounts:refreshSidebarCounts",
  "brands/whatsapp:forceSyncStatus",
  "brands/whatsapp:syncWhatsAppStatus",
]);

/** A dropped connection under a background call: nothing to tell anyone. */
export function isBackgroundDrop(udfPath: string, message: string): boolean {
  return BACKGROUND_CALLS.has(udfPath) && isConnectionDrop(message);
}

// When the page was last hidden / offline — a locked iPhone only reports a
// dropped call once it wakes, by which time the page reads "visible" again.
let lastHiddenAt = 0;
let lastOfflineAt = 0;
const RECENT_MS = 60_000;

/** What the browser can say about its connection, right now and a moment ago. */
export function clientDiagnostics(client: ConvexReactClient | null): { net: NetDiagnostics; build?: string; viewport?: string } {
  const nav: any = typeof navigator !== "undefined" ? navigator : {};
  const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
  let state: any = null;
  try {
    state = (client as any)?.connectionState?.() ?? null;
  } catch {
    state = null;
  }
  const now = Date.now();
  const net: NetDiagnostics = {
    ...(typeof nav.onLine === "boolean" ? { online: nav.onLine } : {}),
    ...(conn?.effectiveType ? { type: String(conn.effectiveType) } : {}),
    ...(typeof conn?.rtt === "number" ? { rtt: conn.rtt } : {}),
    ...(typeof conn?.downlink === "number" ? { downlink: conn.downlink } : {}),
    ...(conn?.saveData ? { saveData: true } : {}),
    ...(typeof document !== "undefined" ? { visibility: document.visibilityState } : {}),
    ...(lastHiddenAt && now - lastHiddenAt < RECENT_MS ? { backgroundRecently: true } : {}),
    ...(lastOfflineAt && now - lastOfflineAt < RECENT_MS ? { offlineRecently: true } : {}),
    ...(state
      ? {
          connected: !!state.isWebSocketConnected,
          reconnects: Math.max(0, Number(state.connectionCount ?? 1) - 1),
          retries: Number(state.connectionRetries ?? 0),
        }
      : {}),
    ...(typeof performance !== "undefined" ? { secondsOnPage: Math.round(performance.now() / 1000) } : {}),
  };
  const build = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 7);
  return {
    net,
    ...(build ? { build } : {}),
    ...(typeof window !== "undefined" ? { viewport: `${window.innerWidth}×${window.innerHeight}` } : {}),
  };
}

export type ParsedFailure = {
  kind: "mutation" | "action" | "query";
  udfPath: string;
  message: string;
  requestId?: string;
};

/** Pull the function and the message out of one Convex client log line; null when it is not a failure worth a report. */
export function parseConvexFailure(line: unknown): ParsedFailure | null {
  if (typeof line !== "string") return null;
  const m = LOG_LINE.exec(line);
  if (!m) return null;
  const [, letter, udfPath, rest] = m;
  const message = rest.trim();
  if (!message) return null;
  // ConvexError → deliberate; already handled where it was thrown — EXCEPT a
  // surfaced internal failure (helpers/surfaceErrors.ts): an unexpected error
  // whose real text rides in the payload. A mutation that throws can't log
  // itself, so this report is the only place that text is ever kept
  // (Mutoni's swaps, Oct 2026).
  const surfacedInternal = /"kind"\s*:\s*"internal"/.test(message);
  if (!surfacedInternal && (/^ConvexError\b/.test(message) || /Uncaught ConvexError/.test(message))) return null;
  if (SELF_REPORTING.some((prefix) => udfPath.startsWith(prefix))) return null;
  const requestId = message.match(/Request ID:\s*([A-Za-z0-9_-]+)/i)?.[1];
  return {
    kind: letter === "M" ? "mutation" : letter === "A" ? "action" : "query",
    udfPath,
    message,
    requestId,
  };
}

const recent = new Map<string, number>();

/** True the first time a given failure is seen inside the window, false for repeats. */
export function shouldReport(failure: ParsedFailure, now = Date.now()): boolean {
  const key = `${failure.udfPath}|${failure.message.replace(/Request ID:\s*[A-Za-z0-9_-]+/i, "").slice(0, 200)}`;
  const last = recent.get(key);
  if (last !== undefined && now - last < REPEAT_WINDOW_MS) return false;
  recent.set(key, now);
  if (recent.size > 200) {
    for (const [k, t] of recent) if (now - t > REPEAT_WINDOW_MS) recent.delete(k);
  }
  return true;
}

let installed: WeakSet<object> | null = null;
let reportingClient: ConvexReactClient | null = null;

/** A path with its ids folded, so one page's crashes group: /pos/estimates/:id. */
export function pagePattern(pathname: string): string {
  return pathname.replace(/\/[A-Za-z0-9_-]{20,}(?=\/|$)/g, "/:id");
}

/**
 * What to log for a page that crashed into an error boundary. A failed Convex
 * call names its function, so the report carries it. The source always starts
 * with "page:" — never "pos/", which alerts the admins as a refused sale.
 * ConvexErrors are reported too: a business rule shown in a toast is
 * deliberate, a page that cannot open because of one is not.
 */
export function pageCrashReport(error: unknown, pathname: string): { source: string; message: string } {
  const raw = typeof (error as any)?.message === "string" ? (error as any).message : String(error ?? "");
  const message = raw.trim() || "The page crashed without a message";
  const fn = /^\[CONVEX [MAQ]\(([^)]+)\)\]/.exec(message)?.[1];
  return { source: `page:${fn ?? pagePattern(pathname)}`, message };
}

/**
 * Log a page that crashed (called by the POS error boundary). A query that
 * fails is thrown into the page by useQuery and never passes through the
 * client's logger, so without this a page that will not open leaves nothing
 * on the Errors page — Nakasero Estates' estimates crashed for four days
 * unseen (Oct 2026). Same once-a-minute limit as the logger's reports.
 */
export function reportPageCrash(error: unknown, extra?: Record<string, unknown>): void {
  if (typeof window === "undefined" || !reportingClient) return;
  try {
    const { source, message } = pageCrashReport(error, window.location.pathname);
    if (!shouldReport({ kind: "query", udfPath: source, message })) return;
    void reportingClient
      .mutation(api.platformAdmin.errorLog.reportClientError, {
        source,
        message: message.slice(0, 4000),
        brandId: (reportingBrandId as Id<"brands"> | null) ?? undefined,
        context: {
          via: "page",
          digest: (error as any)?.digest,
          stack: String((error as any)?.stack ?? "").split("\n").slice(0, 8).join("\n"),
          page: window.location.pathname,
          host: window.location.host,
          userAgent: navigator.userAgent.slice(0, 200),
          ...clientDiagnostics(reportingClient),
          ...extra,
        },
      })
      .catch(() => {
        /* best effort */
      });
  } catch {
    /* reporting must never throw into the error page */
  }
}

/**
 * What a failed mutation/action call actually delivered to the browser.
 *
 * On a PRODUCTION deployment every failure's message is "[Request ID: …]
 * Server Error" — a ConvexError included (probed 7 Oct 2026). The words we
 * wrote for the user ride only in `error.data`. Reading the logged message
 * alone therefore filed every business rule ("the swap amounts don't
 * balance", "one message per turn") as "Convex redacted the text", and
 * missed the real text of a surfaced internal failure.
 *
 *   data is a business rule      → not reported (the user was told);
 *   data.kind === "internal"     → reported WITH its real text;
 *   no data                      → reported (a plain Error or a refused argument).
 */
export function classifyCallFailure(error: unknown): { report: boolean; internal: boolean; message: string; where?: string; requestId?: string } {
  const raw = String((error as any)?.message ?? error ?? "").trim() || "Unknown error";
  const requestId = raw.match(/Request ID:\s*([A-Za-z0-9_-]+)/i)?.[1];
  const data = (error as any)?.data;
  if (data !== undefined && data !== null) {
    if (typeof data === "object" && data.kind === "internal" && typeof data.message === "string") {
      return { report: true, internal: true, message: data.message, where: typeof data.where === "string" ? data.where : undefined, requestId };
    }
    return { report: false, internal: false, message: typeof data === "string" ? data : JSON.stringify(data), requestId };
  }
  return { report: true, internal: false, message: raw, requestId };
}

const FUNCTION_NAME = Symbol.for("functionName");

/** "orders/swaps:swapOrderItems" from a function reference (or its name). */
export function functionNameOf(ref: unknown): string {
  if (typeof ref === "string") return ref;
  try {
    const name = (ref as any)?.[FUNCTION_NAME];
    return typeof name === "string" ? name : "unknown";
  } catch {
    return "unknown";
  }
}

function reportCallFailure(client: ConvexReactClient, kind: "mutation" | "action", ref: unknown, error: unknown): void {
  try {
    const udfPath = functionNameOf(ref);
    if (SELF_REPORTING.some((prefix) => udfPath.startsWith(prefix))) return;
    const c = classifyCallFailure(error);
    if (!c.report) return;
    if (isBackgroundDrop(udfPath, c.message)) return;
    const failure: ParsedFailure = { kind, udfPath, message: c.message, requestId: c.requestId };
    if (!shouldReport(failure)) return;
    void client
      .mutation(api.platformAdmin.errorLog.reportClientError, {
        source: udfPath,
        message: c.message.slice(0, 4000),
        brandId: (reportingBrandId as Id<"brands"> | null) ?? undefined,
        context: {
          via: "client",
          kind,
          requestId: c.requestId,
          ...(c.internal ? { internal: true, where: c.where } : {}),
          page: window.location.pathname,
          host: window.location.host,
          userAgent: navigator.userAgent.slice(0, 200),
          ...clientDiagnostics(client),
        },
      })
      .catch(() => {
        /* the report is best effort; never surface its own failure */
      });
  } catch {
    /* never let reporting throw into the caller */
  }
}

/**
 * Attach the reporter to the app's Convex client. Idempotent per client;
 * a no-op during server rendering.
 *
 * Every failed mutation and action passes through `client.mutation` /
 * `client.action` (useMutation and useAction call them), so the reporter
 * wraps those and reads the error the caller receives — its `data` included.
 * The caller still gets the very same promise. (It used to listen to the
 * client's log lines, which carry only the redacted message.)
 */
export function installConvexErrorReporter(client: ConvexReactClient): void {
  if (typeof window === "undefined") return;
  reportingClient = client;
  installed ??= new WeakSet();
  if (installed.has(client)) return;
  installed.add(client);

  // Remember a locked screen or a lost signal for a minute (clientDiagnostics).
  try {
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") lastHiddenAt = Date.now();
    });
    window.addEventListener("pagehide", () => {
      lastHiddenAt = Date.now();
    });
    window.addEventListener("offline", () => {
      lastOfflineAt = Date.now();
    });
  } catch {
    /* diagnostics are a nicety */
  }

  for (const kind of ["mutation", "action"] as const) {
    const original = (client as any)[kind];
    if (typeof original !== "function") continue;
    const bound = original.bind(client);
    (client as any)[kind] = (ref: unknown, ...rest: unknown[]) => {
      const result = bound(ref, ...rest);
      // A side branch: the caller's own await sees the same rejection.
      Promise.resolve(result).catch((error: unknown) => reportCallFailure(client, kind, ref, error));
      return result;
    };
  }
}
